import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { RebookButton } from "@/components/KpiDrilldown";
import { PatientMessageDialog } from "@/components/PatientMessageDialog";
import { RefillsDashboardCard } from "@/components/RefillsDashboardCard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Mail, Clock, User, BellRing, Send, Users, CheckCircle2, Zap, BarChart3, RotateCcw, List, CalendarDays, UserCheck, CalendarRange, LayoutGrid, Heart } from "lucide-react";

const _CHECKIN = new Set(["checked in", "checked-in", "arrived", "in room", "in session", "checkedin"]);
const _checkedIn = (s) => _CHECKIN.has(String(s || "").trim().toLowerCase());

const _ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const _localISO = (d) => `${_ymd(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
const _monthStart = () => { const n = new Date(); return _ymd(new Date(n.getFullYear(), n.getMonth(), 1)); };
const _monthEnd = () => { const n = new Date(); return _ymd(new Date(n.getFullYear(), n.getMonth() + 1, 0)); };

const TYPE_COLOR = {
  "New Patient Consult": "bg-cyan-500",
  "Follow-up": "bg-blue-500",
  "Lab Review": "bg-violet-500",
  "Procedure": "bg-emerald-500",
  "Telehealth Check-in": "bg-amber-500",
};
const REMINDER_BADGE = {
  confirmed: { label: "✓ Confirmed", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sms_sent: { label: "SMS Sent", c: "bg-violet-50 text-violet-700 border-violet-200" },
  email_confirmed: { label: "Confirmed", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pending", c: "bg-amber-50 text-amber-700 border-amber-200" },
  rescheduled: { label: "Rescheduled", c: "bg-slate-100 text-slate-600 border-slate-200" },
};
const RISK = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// DrChrono appointment status → badge (handled separately from the local check-in pill)
const STATUS_BADGE = {
  "confirmed": { label: "Confirmed", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "complete": { label: "Complete", c: "bg-slate-100 text-slate-600 border-slate-200" },
  "cancelled": { label: "Cancelled", c: "bg-red-50 text-red-700 border-red-200" },
  "canceled": { label: "Cancelled", c: "bg-red-50 text-red-700 border-red-200" },
  "no show": { label: "No Show", c: "bg-red-50 text-red-700 border-red-200" },
  "rescheduled": { label: "Rescheduled", c: "bg-amber-50 text-amber-700 border-amber-200" },
  "not confirmed": { label: "Not Confirmed", c: "bg-slate-100 text-slate-500 border-slate-200" },
};
const statusBadge = (s) => STATUS_BADGE[String(s || "").trim().toLowerCase()];

// Colored block style for the time-grid, by DrChrono status then appointment type
function blockClasses(a) {
  const s = String(a.status || "").trim().toLowerCase();
  if (s === "cancelled" || s === "canceled" || s === "no show") return "bg-red-50 border-red-200 text-red-700 line-through";
  if (s === "complete") return "bg-slate-100 border-slate-200 text-slate-500";
  if (a.confirmed || a.reminder_status === "confirmed" || s === "confirmed") return "bg-emerald-50 border-emerald-300 text-emerald-800";
  return "bg-cyan-50 border-cyan-300 text-cyan-800";
}

function MonthGrid({ appts, onReschedule, onMessage }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDate = {};
  const byId = {};
  appts.forEach((a) => {
    byId[a.id] = a;
    const d = new Date(a.datetime);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    (byDate[key] = byDate[key] || []).push(a);
  });
  const dropOnDay = (e, d) => {
    e.preventDefault();
    if (!d) return;
    const id = e.dataTransfer.getData("text/plain");
    const a = byId[id];
    if (!a) return;
    const old = new Date(a.datetime);
    onReschedule(id, _localISO(new Date(year, month, d, old.getHours(), old.getMinutes(), 0)));
  };
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="calendar-month-grid">
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant="outline" data-testid="month-prev" onClick={() => setCursor(new Date(year, month - 1, 1))}>‹ Prev</Button>
        <h3 className="font-heading font-bold text-slate-900">{monthLabel}</h3>
        <Button size="sm" variant="outline" data-testid="month-next" onClick={() => setCursor(new Date(year, month + 1, 1))}>Next ›</Button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-semibold text-slate-400 mb-1.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          const list = d ? (byDate[`${year}-${month}-${d}`] || []) : [];
          return (
            <div key={i} onDragOver={d ? (e) => e.preventDefault() : undefined} onDrop={(e) => dropOnDay(e, d)}
              className={`min-h-[84px] rounded-lg border p-1.5 text-left ${d ? "bg-white border-slate-100" : "bg-slate-50/50 border-transparent"} ${isToday(d) ? "ring-2 ring-cyan-400" : ""}`}>
              {d && <div className="text-[11px] font-semibold text-slate-500 mb-1">{d}</div>}
              <div className="space-y-1">
                {list.slice(0, 3).map((a) => (
                  <div key={a.id} draggable data-testid={`month-appointment-${a.id}`} onClick={() => onMessage(a)}
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", a.id); e.dataTransfer.effectAllowed = "move"; }}
                    className={`text-[10px] leading-tight rounded px-1.5 py-0.5 truncate border cursor-move ${blockClasses(a)}`}
                    title={`${new Date(a.datetime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} · ${a.patient_name} · ${a.type}`}>
                    {new Date(a.datetime).toLocaleTimeString("en-US", { hour: "numeric" })} {a.patient_name}
                  </div>
                ))}
                {list.length > 3 && <div className="text-[10px] text-slate-400 px-1.5">+{list.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ROW_H = 56;
const BLOCK_MIN = 45;
function layoutDay(dayAppts) {
  const items = dayAppts
    .map((a) => { const dt = new Date(a.datetime); const start = dt.getHours() * 60 + dt.getMinutes(); return { a, dt, start, end: start + BLOCK_MIN }; })
    .sort((x, y) => x.start - y.start);
  const clusters = [];
  let cur = [], curEnd = -1;
  items.forEach((it) => {
    if (cur.length && it.start >= curEnd) { clusters.push(cur); cur = []; curEnd = -1; }
    cur.push(it); curEnd = Math.max(curEnd, it.end);
  });
  if (cur.length) clusters.push(cur);
  const out = [];
  clusters.forEach((cluster) => {
    const colEnds = [];
    cluster.forEach((it) => {
      let placed = false;
      for (let i = 0; i < colEnds.length; i++) { if (it.start >= colEnds[i]) { it.col = i; colEnds[i] = it.end; placed = true; break; } }
      if (!placed) { it.col = colEnds.length; colEnds.push(it.end); }
    });
    cluster.forEach((it) => { it.ncols = colEnds.length; out.push(it); });
  });
  return out;
}

function TimeGrid({ appts, mode, onReschedule, onMessage }) {
  const today = new Date();
  const [anchor, setAnchor] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 60000); return () => clearInterval(t); }, []);

  let days;
  if (mode === "week") {
    const start = new Date(anchor); start.setDate(anchor.getDate() - anchor.getDay());
    days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  } else {
    days = [new Date(anchor)];
  }
  const dayKeys = new Set(days.map((d) => _ymd(d)));
  const windowAppts = appts.filter((a) => dayKeys.has(_ymd(new Date(a.datetime))));

  let minH = 8, maxH = 18;
  windowAppts.forEach((a) => { const h = new Date(a.datetime).getHours(); if (h < minH) minH = h; if (h + 1 > maxH) maxH = h + 1; });
  minH = Math.max(6, minH); maxH = Math.min(21, Math.max(maxH, minH + 4));
  const hours = Array.from({ length: maxH - minH }, (_, i) => minH + i);
  const nowDate = new Date(nowTick);
  const nowMin = nowDate.getHours() * 60 + nowDate.getMinutes();
  const nowTop = (nowMin / 60 - minH) * ROW_H;
  const showNow = nowMin >= minH * 60 && nowMin <= maxH * 60;

  const shift = (n) => { const d = new Date(anchor); d.setDate(anchor.getDate() + n); setAnchor(d); };
  const label = mode === "week"
    ? `${days[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
    : anchor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const isToday = (d) => _ymd(d) === _ymd(today);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm overflow-x-auto" data-testid="calendar-time-grid">
      <div className="flex items-center justify-between mb-3">
        <Button size="sm" variant="outline" data-testid="grid-prev" onClick={() => shift(mode === "week" ? -7 : -1)}>‹ Prev</Button>
        <div className="flex items-center gap-3">
          <h3 className="font-heading font-bold text-slate-900">{label}</h3>
          <button data-testid="grid-today" onClick={() => setAnchor(new Date(today.getFullYear(), today.getMonth(), today.getDate()))}
            className="text-xs font-semibold text-cyan-600 hover:text-cyan-700">Today</button>
        </div>
        <Button size="sm" variant="outline" data-testid="grid-next" onClick={() => shift(mode === "week" ? 7 : 1)}>Next ›</Button>
      </div>

      <div className="min-w-[640px]">
        <div className="flex border-b border-slate-100">
          <div className="w-14 shrink-0" />
          {days.map((d, i) => (
            <div key={i} className={`flex-1 text-center py-2 ${isToday(d) ? "bg-cyan-50 rounded-t-lg" : ""}`}>
              <div className="text-[11px] font-semibold text-slate-400">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
              <div className={`text-sm font-bold ${isToday(d) ? "text-cyan-700" : "text-slate-800"}`}>{d.getDate()}</div>
            </div>
          ))}
        </div>

        <div className="flex relative" style={{ height: hours.length * ROW_H }}>
          <div className="w-14 shrink-0">
            {hours.map((h) => (
              <div key={h} style={{ height: ROW_H }} className="text-[11px] text-slate-400 text-right pr-2 -translate-y-2">
                {new Date(2000, 0, 1, h).toLocaleTimeString("en-US", { hour: "numeric" })}
              </div>
            ))}
          </div>
          {days.map((d, di) => {
            const dayAppts = windowAppts.filter((a) => _ymd(new Date(a.datetime)) === _ymd(d));
            const positioned = layoutDay(dayAppts);
            return (
              <div key={di}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  let raw; try { raw = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
                  const { id, grabMin = 0 } = raw || {};
                  if (!id) return;
                  const pointerMin = ((e.clientY - rect.top) / ROW_H) * 60 + minH * 60;
                  let newStart = Math.round((pointerMin - grabMin) / 15) * 15;
                  newStart = Math.max(minH * 60, Math.min(newStart, maxH * 60 - BLOCK_MIN));
                  const hh = Math.floor(newStart / 60), mm = newStart % 60;
                  onReschedule(id, `${_ymd(d)}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`);
                }}
                className={`flex-1 relative border-l border-slate-100 ${isToday(d) ? "bg-cyan-50/30" : ""}`}>
                {hours.map((h, hi) => <div key={hi} style={{ height: ROW_H }} className="border-b border-slate-50" />)}
                {showNow && isToday(d) && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowTop }} data-testid="now-line">
                    <div className="absolute -left-1 -top-[4px] h-2.5 w-2.5 rounded-full bg-red-500 shadow" />
                    <div className="h-[2px] bg-red-500" />
                  </div>
                )}
                {positioned.map(({ a, dt, start, col, ncols }) => {
                  const top = (start / 60 - minH) * ROW_H;
                  const widthPct = 100 / ncols;
                  const isNoShow = ["no show", "noshow", "no_show"].includes(String(a.status || "").trim().toLowerCase());
                  const block = (
                    <div key={a.id} data-testid={`grid-appointment-${a.id}`} draggable onClick={isNoShow ? undefined : () => onMessage(a)}
                      onDragStart={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.dataTransfer.setData("text/plain", JSON.stringify({ id: a.id, grabMin: ((e.clientY - r.top) / ROW_H) * 60 })); e.dataTransfer.effectAllowed = "move"; }}
                      className={`absolute rounded-md border px-1.5 py-1 text-[10px] leading-tight overflow-hidden shadow-sm cursor-pointer ${blockClasses(a)}`}
                      style={{ top, height: ROW_H * 0.82, left: `calc(${col * widthPct}% + 2px)`, width: `calc(${widthPct}% - 4px)` }}
                      title={`${dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} · ${a.patient_name} · ${a.type} · ${a.provider}${a.status ? ` · ${a.status}` : ""}`}>
                      <div className="font-semibold truncate">{dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} {a.patient_name}</div>
                      <div className="truncate opacity-80">{isNoShow ? "No show · tap to rebook" : a.type}</div>
                    </div>
                  );
                  if (!isNoShow) return block;
                  return (
                    <Popover key={a.id}>
                      <PopoverTrigger asChild>{block}</PopoverTrigger>
                      <PopoverContent data-testid={`noshow-popover-${a.id}`} className="w-64 p-4 space-y-2">
                        <div className="font-semibold text-slate-900 text-sm">{a.patient_name}</div>
                        <div className="text-xs text-slate-500">{dt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {a.provider}</div>
                        <div className="text-xs font-semibold text-red-600">No show</div>
                        <RebookButton row={{ appointment_id: a.id, name: a.patient_name, phone: a.patient_phone || "x",
                          rebook_text_sent_at: a.rebook_text_sent_at || a.noshow_followup_sent }} />
                        <Button data-testid={`noshow-message-${a.id}`} size="sm" variant="outline" className="h-7 text-xs" onClick={() => onMessage(a)}>
                          <Send className="h-3.5 w-3.5 mr-1" />Text, video or file
                        </Button>
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function groupByDay(appts) {
  const groups = {};
  appts.forEach((a) => {
    const key = new Date(a.datetime).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    (groups[key] = groups[key] || []).push(a);
  });
  return groups;
}

function LeadPill({ active, onClick, disabled, testid, children }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
        active ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/40" : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      {children}
    </button>
  );
}

const VIEWS = [
  { id: "list", label: "List", icon: List },
  { id: "day", label: "Day", icon: Clock },
  { id: "week", label: "Week", icon: LayoutGrid },
  { id: "month", label: "Month", icon: CalendarDays },
];

export default function CalendarView() {
  const qc = useQueryClient();
  const { data: appts = [] } = useQuery({ queryKey: ["appointments"], queryFn: async () => (await api.get("/appointments")).data });
  const { data: reminders } = useQuery({ queryKey: ["reminders-status"], queryFn: async () => (await api.get("/reminders/status")).data });
  const { data: report } = useQuery({ queryKey: ["reminders-report"], queryFn: async () => (await api.get("/reminders/report")).data });

  const remind = useMutation({
    mutationFn: async ({ id, channel }) => (await api.post(`/appointments/${id}/remind`, { channel })).data,
    onSuccess: (d) => { toast.success(d.message); qc.invalidateQueries({ queryKey: ["appointments"] }); },
  });

  const checkin = useMutation({
    mutationFn: async (id) => (await api.post(`/appointments/${id}/checkin`)).data,
    onSuccess: (d) => {
      toast.success(d.deducted?.package_id ? `Checked in · 1 ${d.deducted.procedure_name} session deducted (${d.deducted.remaining} left)` : "Patient checked in");
      qc.invalidateQueries({ queryKey: ["appointments"] });
      qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: () => toast.error("Could not check in"),
  });

  const saveSettings = useMutation({
    mutationFn: async (patch) => (await api.post("/reminders/settings", {
      enabled: reminders?.enabled ?? true,
      lead_48: reminders?.lead_48 ?? true,
      lead_24: reminders?.lead_24 ?? false,
      same_day_high_risk: reminders?.same_day_high_risk ?? false,
      noshow_followup: reminders?.noshow_followup ?? false,
      post_checkin: reminders?.post_checkin ?? false,
      ...patch,
    })).data,
    onSuccess: (d, patch) => {
      if ("enabled" in patch) toast.success(d.enabled ? "Automated reminders turned ON" : "Automated reminders turned OFF");
      else toast.success("Reminder settings saved");
      qc.invalidateQueries({ queryKey: ["reminders-status"] });
    },
  });

  const runNow = useMutation({
    mutationFn: async () => (await api.post("/reminders/run")).data,
    onSuccess: (d) => {
      toast.success(d.skipped === "disabled" ? "Reminders are turned off — enable them first" : `Sent ${d.sent} reminder${d.sent === 1 ? "" : "s"} (${d.eligible} due now)`);
      qc.invalidateQueries({ queryKey: ["reminders-status"] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
    },
    onError: () => toast.error("Could not send reminders"),
  });

  const notifyMove = useMutation({
    mutationFn: async (id) => (await api.post(`/appointments/${id}/notify-reschedule`)).data,
    onSuccess: (d) => toast.success(
      d.status === "opted_out" ? "Patient has opted out of texts"
        : d.status === "simulated" ? "New time queued (SMS not configured)"
        : d.status === "sent" ? "New time texted to the patient"
        : "Could not text the patient"),
    onError: (e) => toast.error(e.response?.data?.detail || "Could not text the patient"),
  });

  const reschedule = useMutation({
    mutationFn: async ({ id, datetime }) => (await api.post(`/appointments/${id}/reschedule`, { datetime })).data,
    onMutate: async ({ id, datetime }) => {
      await qc.cancelQueries({ queryKey: ["appointments"] });
      const prev = qc.getQueryData(["appointments"]) || [];
      const prevAppt = prev.find((a) => a.id === id);
      qc.setQueryData(["appointments"], prev.map((a) => (a.id === id ? { ...a, datetime } : a)));
      return { prev, prevDatetime: prevAppt?.datetime, prevAppt };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["appointments"], ctx.prev);
      toast.error("Could not move appointment");
    },
    onSuccess: (d, vars, ctx) => {
      if (vars._undo) { toast.success("Move undone"); return; }
      const when = new Date(d.datetime).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
      const canText = !!(reminders?.sms_configured && ctx?.prevAppt?.patient_phone);
      toast.success(`Moved to ${when}`, {
        ...(canText ? { action: { label: "Text patient", onClick: () => notifyMove.mutate(vars.id) } } : {}),
        ...(ctx?.prevDatetime ? { cancel: { label: "Undo", onClick: () => reschedule.mutate({ id: vars.id, datetime: ctx.prevDatetime, _undo: true }) } } : {}),
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["appointments"] }),
  });
  const onReschedule = (id, datetime) => reschedule.mutate({ id, datetime });

  const lastRun = reminders?.last_run;
  const busy = saveSettings.isPending;
  const [view, setView] = useState("week");
  const [msgAppt, setMsgAppt] = useState(null);
  const [isWide, setIsWide] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setIsWide(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [provider, setProvider] = useState("all");
  const [range, setRange] = useState({ from: _monthStart(), to: _monthEnd() });
  const [draft, setDraft] = useState(range);
  const [rangeOpen, setRangeOpen] = useState(false);

  const providers = Array.from(new Set(appts.map((a) => a.provider).filter(Boolean))).sort();
  const providerFiltered = appts.filter((a) => provider === "all" || a.provider === provider);

  const isCurrentMonth = range.from === _monthStart() && range.to === _monthEnd();
  const rangeLabel = isCurrentMonth
    ? new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : `${new Date(range.from + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.to + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  const inRange = providerFiltered.filter((a) => {
    const key = _ymd(new Date(a.datetime));
    return key >= range.from && key <= range.to;
  });
  const groups = groupByDay(inRange);

  const controls = (
        <div className="flex items-center gap-2 flex-wrap lg:justify-end">
          {providers.length >= 1 && (
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="h-9 w-full sm:w-[190px]" data-testid="provider-filter-trigger">
                <Users className="h-4 w-4 text-cyan-600 mr-1.5" />
                <SelectValue placeholder="All providers" />
              </SelectTrigger>
              <SelectContent data-testid="provider-filter-content">
                <SelectItem value="all" data-testid="provider-option-all">All providers</SelectItem>
                {providers.map((p) => <SelectItem key={p} value={p} data-testid={`provider-option-${p}`}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {view === "list" && (
            <Popover open={rangeOpen} onOpenChange={(o) => { setRangeOpen(o); if (o) setDraft(range); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="date-range-button" className="gap-1.5 h-9">
                  <CalendarRange className="h-4 w-4 text-cyan-600" />
                  <span className="font-medium">{rangeLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4" data-testid="date-range-popover">
                <div className="text-sm font-heading font-bold text-slate-900 mb-3">Show appointments between</div>
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">From</label>
                    <Input type="date" data-testid="date-range-from" value={draft.from}
                      onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">To</label>
                    <Input type="date" data-testid="date-range-to" value={draft.to}
                      onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} className="mt-1" />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <button data-testid="date-range-reset"
                    onClick={() => { const r = { from: _monthStart(), to: _monthEnd() }; setDraft(r); setRange(r); setRangeOpen(false); }}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700">Reset to this month</button>
                  <Button size="sm" data-testid="date-range-apply" className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    disabled={!draft.from || !draft.to || draft.from > draft.to}
                    onClick={() => { setRange(draft); setRangeOpen(false); }}>Apply</Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="calendar-view-toggle">
            {VIEWS.map((v) => (
              <button key={v.id} data-testid={`view-${v.id}-button`} onClick={() => setView(v.id)}
                className={`flex items-center gap-1.5 text-sm font-medium px-2.5 sm:px-3 py-1.5 rounded-md transition-colors ${view === v.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                <v.icon className="h-4 w-4" /> {v.label}
              </button>
            ))}
          </div>
        </div>
  );

  return (
    <Layout title="Appointment Calendar" subtitle="Reduce no-shows with proactive reminders"
      actions={isWide ? controls : null}>
      {!isWide && <div className="mb-5" data-testid="calendar-mobile-controls">{controls}</div>}
      <RefillsDashboardCard className="mb-6" />
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        data-testid="auto-reminders-card"
        className="bg-slate-950 text-white rounded-xl p-5 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
              <BellRing className="h-5 w-5 text-cyan-300" />
            </span>
            <div>
              <div className="font-heading font-bold flex items-center gap-2">
                Automated SMS Reminders
                <span data-testid="auto-reminders-state" className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${reminders?.enabled ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" : "bg-slate-700 text-slate-300 border-slate-600"}`}>
                  {reminders?.enabled ? "ON" : "OFF"}
                </span>
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                Patients reply <span className="text-cyan-300 font-semibold">C</span> to confirm · {reminders?.schedule || "Daily each morning (ET)"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 ml-auto">
            <div className="text-center">
              <div data-testid="reminders-eligible-count" className="font-mono-metric font-bold text-xl text-cyan-300 flex items-center gap-1.5 justify-center">
                <Users className="h-4 w-4" /> {reminders?.eligible_now ?? 0}
              </div>
              <div className="text-[11px] text-slate-400">due now</div>
            </div>
            {lastRun && (
              <div className="text-center hidden sm:block">
                <div className="font-mono-metric font-bold text-xl text-white">{lastRun.sent ?? 0}</div>
                <div className="text-[11px] text-slate-400">sent last run</div>
              </div>
            )}
            <Switch data-testid="auto-reminders-toggle" checked={!!reminders?.enabled}
              onCheckedChange={(v) => saveSettings.mutate({ enabled: v })} disabled={busy} />
            <Button data-testid="send-reminders-now-button" size="sm"
              onClick={() => runNow.mutate()} disabled={runNow.isPending || !reminders?.sms_configured}
              className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Send className={`h-3.5 w-3.5 mr-1.5 ${runNow.isPending ? "animate-pulse" : ""}`} /> Send now
            </Button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap items-center gap-3">
          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Send when</span>
          <LeadPill testid="lead-48-pill" active={!!reminders?.lead_48} disabled={busy}
            onClick={() => saveSettings.mutate({ lead_48: !reminders?.lead_48 })}>48h before</LeadPill>
          <LeadPill testid="lead-24-pill" active={!!reminders?.lead_24} disabled={busy}
            onClick={() => saveSettings.mutate({ lead_24: !reminders?.lead_24 })}>24h before</LeadPill>
          <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-800">
            <Zap className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-xs text-slate-300">Same-day nudge for high-risk</span>
            <Switch data-testid="same-day-toggle" checked={!!reminders?.same_day_high_risk} disabled={busy}
              onCheckedChange={(v) => saveSettings.mutate({ same_day_high_risk: v })} />
          </div>
          <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-800">
            <RotateCcw className="h-3.5 w-3.5 text-cyan-300" />
            <span className="text-xs text-slate-300">Re-book no-shows next morning</span>
            <Switch data-testid="noshow-toggle" checked={!!reminders?.noshow_followup} disabled={busy}
              onCheckedChange={(v) => saveSettings.mutate({ noshow_followup: v })} />
          </div>
          <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-800">
            <Heart className="h-3.5 w-3.5 text-rose-300" />
            <span className="text-xs text-slate-300">Check in a day after each visit</span>
            <Switch data-testid="post-checkin-toggle" checked={!!reminders?.post_checkin} disabled={busy}
              onCheckedChange={(v) => saveSettings.mutate({ post_checkin: v })} />
          </div>
        </div>
      </motion.div>

      {report?.weeks?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          data-testid="reminders-report-card"
          className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-cyan-600" />
            <h3 className="font-heading font-bold text-slate-900">Last 4 Weeks · Reminder Performance</h3>
            <span className="text-xs text-slate-400 ml-auto">
              {report.totals.reminders_sent} sent · {report.totals.confirmations} confirmed · {report.totals.followups_sent} re-booked · {report.totals.no_shows} no-shows
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {report.weeks.map((w, i) => (
              <div key={i} data-testid={`report-week-${i}`} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                <div className="text-[11px] font-semibold text-slate-500 mb-2">Wk of {w.label}</div>
                <div className="flex items-center gap-1.5 text-sm text-slate-900 font-semibold">
                  <Send className="h-3.5 w-3.5 text-cyan-600" /> {w.reminders_sent}
                  <span className="text-[11px] text-slate-400 font-normal">sent</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-emerald-700 font-semibold mt-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {w.confirmations}
                  <span className="text-[11px] text-slate-400 font-normal">confirmed</span>
                </div>
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>No-show</span><span className={`font-semibold ${w.no_show_rate > 15 ? "text-red-600" : "text-slate-700"}`}>{w.no_show_rate}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200 mt-1 overflow-hidden">
                    <div className={`h-full rounded-full ${w.no_show_rate > 15 ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, w.no_show_rate)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {view === "month" ? (
        <MonthGrid appts={providerFiltered} onReschedule={onReschedule} onMessage={setMsgAppt} />
      ) : view === "week" || view === "day" ? (
        <TimeGrid appts={providerFiltered} mode={view} onReschedule={onReschedule} onMessage={setMsgAppt} />
      ) : (
      <div className="space-y-6">
        {Object.keys(groups).length === 0 && (
          <div data-testid="no-appointments-in-range" className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
            <CalendarRange className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <div className="font-heading font-bold text-slate-900">No appointments in {rangeLabel}</div>
            <p className="text-sm text-slate-500 mt-1">Use the date button above to pick a different range{provider !== "all" ? " or clear the provider filter" : ""}.</p>
          </div>
        )}
        {Object.entries(groups).map(([day, items], gi) => {
          const ds = items.reduce((s, a) => {
            const c = a.confirmed || a.reminder_status === "confirmed";
            const sent = (a.reminders_sent && a.reminders_sent.length > 0) || a.reminder_status === "sms_sent";
            if (c) s.confirmed += 1; else if (sent) s.awaiting += 1; else s.pending += 1;
            return s;
          }, { confirmed: 0, awaiting: 0, pending: 0 });
          return (
          <motion.div key={day} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: gi * 0.04 }}>
            <div className="flex items-center gap-2.5 mb-3 flex-wrap" data-testid={`day-summary-${gi}`}>
              <h3 className="font-heading font-bold text-slate-900">{day}</h3>
              <span className="text-xs text-slate-400">{items.length} appointments</span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{ds.confirmed} confirmed</span>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{ds.awaiting} awaiting reply</span>
            </div>
            <div className="space-y-2.5">
              {items.map((a) => {
                const rb = REMINDER_BADGE[a.reminder_status] || REMINDER_BADGE.pending;
                const sb = statusBadge(a.status);
                return (
                  <div key={a.id} data-testid={`appointment-${a.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-1.5 h-12 rounded-full ${TYPE_COLOR[a.type] || "bg-slate-400"}`} />
                      <div className="text-center w-16 shrink-0">
                        <div className="font-mono-metric font-bold text-slate-900 text-sm">
                          {new Date(a.datetime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5 truncate">
                          <User className="h-3.5 w-3.5 text-slate-400" />
                          <button data-testid={`patient-name-${a.id}`} onClick={() => setMsgAppt(a)} title="Send a text, video or file"
                            className="truncate hover:text-[#13306a] hover:underline underline-offset-2">{a.patient_name}</button>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{a.type} · {a.provider}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {sb && <span data-testid={`status-badge-${a.id}`} className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${sb.c}`}>{sb.label}</span>}
                      <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${RISK[a.no_show_risk] || RISK.low}`} title={a.risk_reason}>
                        {a.no_show_risk === "high" ? "⚠ " : ""}{(a.no_show_risk || "low").toUpperCase()} RISK
                      </span>
                      <span data-testid={`reminder-badge-${a.id}`} className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${rb.c}`}>{rb.label}</span>
                      {_checkedIn(a.status) ? (
                        <span data-testid={`checked-in-${a.id}`} className="text-[11px] font-semibold px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Checked in{a.package_deducted ? " · −1 session" : ""}</span>
                      ) : ["no show", "noshow", "no_show"].includes(String(a.status || "").trim().toLowerCase()) ? (
                        <RebookButton row={{ appointment_id: a.id, name: a.patient_name, phone: a.patient_phone || "x",
                          rebook_text_sent_at: a.rebook_text_sent_at || a.noshow_followup_sent }} />
                      ) : (
                        <Button data-testid={`checkin-button-${a.id}`} size="sm" variant="outline" disabled={checkin.isPending} onClick={() => checkin.mutate(a.id)}>
                          <UserCheck className="h-3.5 w-3.5 mr-1.5" /> Check in
                        </Button>
                      )}
                      <Button data-testid={`send-sms-reminder-button-${a.id}`} size="sm" variant="outline"
                        disabled={remind.isPending} onClick={() => remind.mutate({ id: a.id, channel: "sms" })}>
                        <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> SMS
                      </Button>
                      <Button data-testid={`send-email-reminder-button-${a.id}`} size="sm" variant="outline"
                        disabled={remind.isPending} onClick={() => remind.mutate({ id: a.id, channel: "email" })}>
                        <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
          );
        })}
      </div>
      )}
      <p className="text-xs text-slate-400 mt-6 flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" /> Live SMS reminders are sent to DrChrono patients (respecting quiet hours & opt-outs). Patients who reply “C” are auto-marked Confirmed.
      </p>
      <PatientMessageDialog appt={msgAppt} onClose={() => setMsgAppt(null)} />
    </Layout>
  );
}
